/**
 * @fileoverview Screen Component for Stadium Giant Display: 'Arma la Playlist' (Tile ID 46) on /screen & ScreenV2.
 * Renders high-impact visual layouts for 1080p / 4K UHD LED screens with GPU-accelerated CSS animations (60 FPS),
 * real-time ADSL WebSocket synchronization, interactive QR codes with custom/default central logo,
 * rotating vinyl animation, soundwave equalizer visualizer, and dynamic queue rankings.
 * 
 * Supports 3 Screen Modes:
 *  - Mode 0: CallToAction / Convocatoria (Giant Centered QR with logo, motivational titles)
 *  - Mode 1: NowPlaying / En Reproducción (Featured Song Card, Rotating Vinyl, Soundwave, Fan Credits, Lateral QR)
 *  - Mode 2: Queue / En Cola (Top/Left Current Song, Upcoming 2-3 queued songs with ▲ indicators, Lateral QR)
 * 
 * @module components/Screen/Sections/PlaylistScreen
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import { GetNowPlaying, GetSettings, GetActivePlaylist } from '../../../utilities/BL/Admin/Overlays/Playlist';
import { ConnectWebSocketForPlaylistAdmin, DisconnectWebSocketForPlaylistAdmin } from '../../../utilities/WS/Admin/PlaylistWS';

/**
 * Default fallback configuration for Playlist Screen display.
 * @constant {Object}
 */
const DEFAULT_PLAYLIST_SETTINGS = {
    screenMode: 0, // 0: CallToAction, 1: NowPlaying, 2: Queue
    isScreenQrVisible: true,
    showFanNameOnScreen: true,
    showVoteCountOnScreen: true,
    screenTitle: 'ARMA LA PLAYLIST',
    screenSubtitle: '¿QUÉ CANCIÓN O ARTISTA FALTA EN LA PLAYLIST?',
    screenBackgroundUrl: '',
    qrUrl: '',
    qrCenterLogoUrl: ''
};

/**
 * Normalizes the screenMode property into integer modes (0: CallToAction, 1: NowPlaying, 2: Queue).
 * @param {string|number} mode - Raw mode input from WebSocket or REST API.
 * @returns {number} Normalized integer mode (0, 1, or 2).
 */
const normalizeScreenMode = (mode) => {
    if (mode === undefined || mode === null) return 0;
    if (typeof mode === 'string') {
        const lower = mode.toLowerCase().trim();
        if (lower === '0' || lower === 'calltoaction' || lower === 'call_to_action' || lower === 'cover' || lower === 'convocatoria') {
            return 0;
        }
        if (lower === '1' || lower === 'nowplaying' || lower === 'now_playing' || lower === 'reproduccion') {
            return 1;
        }
        if (lower === '2' || lower === 'queue' || lower === 'cola' || lower === 'top' || lower === '3') {
            return 2;
        }
    }
    const num = Number(mode);
    if (num === 0) return 0;
    if (num === 1) return 1;
    if (num === 2) return 2;
    if (num === 3) return 2;
    return 0;
};

/**
 * Resolves media file paths into fully qualified absolute URLs.
 * @param {string|null} mediaPath - Relative or absolute media location.
 * @returns {string} Fully resolved media URL.
 */
const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
    const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
    return apiBaseEndpointUrl ? `${apiBaseEndpointUrl}${sanitizedMediaPath}` : sanitizedMediaPath;
};

/**
 * PlaylistScreen Component
 * 
 * @function PlaylistScreen
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - WebSocket payload forwarded by Screen router.
 * @returns {React.ReactElement} The rendered stadium display view.
 */
export const PlaylistScreen = ({ payload = null }) => {
    const [settings, setSettings] = useState(DEFAULT_PLAYLIST_SETTINGS);
    const [currentSong, setCurrentSong] = useState(null);
    const [activePlaylist, setActivePlaylist] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState('connecting');

    // =========================================================================
    // 1. CARGA INICIAL DE DATOS DESDE LA API REST
    // =========================================================================
    const loadInitialData = useCallback(async () => {
        try {
            const [settingsRes, nowPlayingRes, activePlaylistRes] = await Promise.all([
                GetSettings(),
                GetNowPlaying(),
                GetActivePlaylist()
            ]);

            if (settingsRes && settingsRes.success && settingsRes.data) {
                setSettings(prev => ({
                    ...prev,
                    ...settingsRes.data,
                    screenMode: normalizeScreenMode(settingsRes.data.screenMode)
                }));
            }

            if (nowPlayingRes && nowPlayingRes.success && nowPlayingRes.data) {
                if (nowPlayingRes.data.currentSong) {
                    setCurrentSong(nowPlayingRes.data.currentSong);
                }
                if (nowPlayingRes.data.screenMode !== undefined) {
                    setSettings(prev => ({
                        ...prev,
                        screenMode: normalizeScreenMode(nowPlayingRes.data.screenMode)
                    }));
                }
            }

            if (activePlaylistRes && activePlaylistRes.success && Array.isArray(activePlaylistRes.data)) {
                setActivePlaylist(activePlaylistRes.data);
            }
        } catch (error) {
            console.error('[PlaylistScreen] Error cargando datos iniciales:', error);
        }
    }, []);

    // =========================================================================
    // 2. MANEJADOR DE MENSAJES WEBSOCKET EN TIEMPO REAL
    // =========================================================================
    const handleWebSocketMessage = useCallback((packet) => {
        if (!packet || typeof packet !== 'object') return;

        const action = packet.action || packet.type;
        const data = packet.data || packet.payload;

        switch (action) {
            case 'now_playing_changed':
            case 'playlist.now_playing':
                if (data) {
                    if (data.currentSong !== undefined) {
                        setCurrentSong(data.currentSong || null);
                    } else if (data.id && data.title) {
                        setCurrentSong(data);
                    }
                    if (data.screenMode !== undefined) {
                        setSettings(prev => ({ ...prev, screenMode: normalizeScreenMode(data.screenMode) }));
                    } else {
                        // Forzar cambio al modo NowPlaying si llega evento now_playing
                        setSettings(prev => ({ ...prev, screenMode: 1 }));
                    }
                }
                break;

            case 'settings_updated':
            case 'playlist.settings':
            case 'playlist.state':
                if (data) {
                    setSettings(prev => ({
                        ...prev,
                        ...data,
                        screenMode: data.screenMode !== undefined ? normalizeScreenMode(data.screenMode) : prev.screenMode
                    }));
                    if (data.currentPlayingSong !== undefined) {
                        setCurrentSong(data.currentPlayingSong);
                    }
                }
                break;

            case 'playlist_updated':
            case 'playlist.queue':
                if (Array.isArray(data)) {
                    setActivePlaylist(data);
                }
                break;

            case 'playlist.cover':
            case 'playlist.screen':
            case 'playlist.project':
            case 'playlist.prepare':
            case 'screen_projected':
                if (data && data.screenMode !== undefined) {
                    setSettings(prev => ({ ...prev, screenMode: normalizeScreenMode(data.screenMode) }));
                }
                break;

            case 'dynamic_reset':
                setCurrentSong(null);
                setActivePlaylist([]);
                break;

            case 'suggestion_received':
            case 'suggestion_updated':
            case 'vote_added':
                // Si la sugerencia afecta a la playlist activa, recargamos el estado
                loadInitialData();
                break;

            default:
                break;
        }
    }, [loadInitialData]);

    // =========================================================================
    // 3. EFECTO: CONEXIÓN WEBSOCKET Y CARGA INICIAL
    // =========================================================================
    useEffect(() => {
        loadInitialData();

        ConnectWebSocketForPlaylistAdmin(
            handleWebSocketMessage,
            (status) => setConnectionStatus(status),
            'pantalla'
        );

        return () => {
            DisconnectWebSocketForPlaylistAdmin();
        };
    }, [loadInitialData, handleWebSocketMessage]);

    // =========================================================================
    // 4. EFECTO: PROCESAR PAYLOAD DE ENRUTADOR PADRE (Screen.js / ScreenV2.js)
    // =========================================================================
    useEffect(() => {
        if (!payload) return;
        handleWebSocketMessage(payload);
    }, [payload, handleWebSocketMessage]);

    // =========================================================================
    // 5. CÁLCULO DE VALORES DERIVADOS Y PROPIEDADES VISUALES
    // =========================================================================
    const activeScreenMode = normalizeScreenMode(settings.screenMode);

    // URL de destino del QR
    const qrTargetUrl = useMemo(() => {
        if (settings.qrUrl && settings.qrUrl.trim()) {
            return settings.qrUrl;
        }
        if (typeof window !== 'undefined' && window.location && window.location.origin) {
            return `${window.location.origin}/playlist`;
        }
        return 'https://cs.interaktia.com/playlist';
    }, [settings.qrUrl]);

    // Logo central del QR
    const qrCenterLogo = useMemo(() => {
        if (settings.qrCenterLogoUrl && settings.qrCenterLogoUrl.trim()) {
            return resolveMediaUrl(settings.qrCenterLogoUrl);
        }
        return '/img/page/adsl-five-bicolor.png';
    }, [settings.qrCenterLogoUrl]);

    // Fondo visual dinámico o institucional
    const dynamicBackgroundStyle = useMemo(() => {
        if (settings.screenBackgroundUrl && settings.screenBackgroundUrl.trim()) {
            return { backgroundImage: `url(${resolveMediaUrl(settings.screenBackgroundUrl)})` };
        }
        return { backgroundImage: 'url(/img/page/Fondo-Azul-Screen.png)' };
    }, [settings.screenBackgroundUrl]);

    // Canción activa a mostrar (prioriza currentSong o canción marcada en activePlaylist)
    const activeSong = useMemo(() => {
        if (currentSong && currentSong.title) return currentSong;
        if (Array.isArray(activePlaylist) && activePlaylist.length > 0) {
            const foundNowPlaying = activePlaylist.find(s => s.isNowPlaying);
            if (foundNowPlaying) return foundNowPlaying;
            return activePlaylist[0];
        }
        return null;
    }, [currentSong, activePlaylist]);

    // Canciones en cola para Modo 2 (próximas 2 o 3 canciones sin contar la que está sonando)
    const upcomingQueue = useMemo(() => {
        if (!Array.isArray(activePlaylist) || activePlaylist.length === 0) return [];
        const activeId = activeSong?.id;
        const remaining = activePlaylist.filter(s => s.id !== activeId && !s.isPlayed);
        return (remaining.length > 0 ? remaining : activePlaylist.filter(s => s.id !== activeId)).slice(0, 3);
    }, [activePlaylist, activeSong]);

    // Renderizado de créditos del aficionado
    const renderFanCredits = (song) => {
        if (!song) return null;
        const hasFan = Boolean(settings.showFanNameOnScreen && song.fanName && song.fanName.trim());
        const hasVotes = Boolean(settings.showVoteCountOnScreen && song.voteCount && song.voteCount > 0);

        if (!hasFan && !hasVotes) return null;

        return (
            <div className="PlaylistScreen__Credits">
                <i className="bi bi-heart-fill PlaylistScreen__CreditsIcon"></i>
                <span>
                    {hasFan && hasVotes ? (
                        <>Pedida por <strong>{song.fanName}</strong> y <strong>{song.voteCount} aficionados más</strong></>
                    ) : hasFan ? (
                        <>Pedida por <strong>{song.fanName}</strong></>
                    ) : (
                        <><strong>{song.voteCount} aficionados</strong> votaron por esta canción</>
                    )}
                </span>
            </div>
        );
    };

    return (
        <div className="PlaylistScreen">
            {/* Fondo dinámico y capas de iluminación LED */}
            <div className="PlaylistScreen__Background" style={dynamicBackgroundStyle} />
            <div className="PlaylistScreen__BackdropOverlay" />
            <div className="PlaylistScreen__GlowLightTop" />
            <div className="PlaylistScreen__GlowLightBottom" />

            {/* HEADER INSTITUCIONAL */}
            <header className="PlaylistScreen__Header">
                <div className="PlaylistScreen__HeaderBrand">
                    <img
                        src="/img/page/adsl-five-bicolor.png"
                        alt="Atlético de San Luis"
                        className="PlaylistScreen__Logo"
                    />
                    <div className="PlaylistScreen__HeaderTitles">
                        <h1 className="PlaylistScreen__HeaderMainTitle">Atlético de San Luis</h1>
                        <span className="PlaylistScreen__HeaderSubTitle">Estadio Libertad Financiera</span>
                    </div>
                </div>

                <div className="PlaylistScreen__LiveBadge">
                    <span className="PlaylistScreen__LiveDot"></span>
                    <span>Música en Vivo</span>
                </div>
            </header>

            {/* CONTENIDO PRINCIPAL POR MODO */}
            <main className="PlaylistScreen__Body">

                {/* =========================================================== */}
                {/* MODO 0: CALL TO ACTION (CONVOCATORIA Y QR GIGANTE) */}
                {/* =========================================================== */}
                {activeScreenMode === 0 && (
                    <section className="PlaylistScreen__Mode0">
                        <div className="PlaylistScreen__HeroBadge">
                            <i className="bi bi-music-note-beamed"></i>
                            <span>Dinámica de Estadio</span>
                        </div>

                        <h2 className="PlaylistScreen__Title">
                            {settings.screenTitle || 'ARMA LA PLAYLIST'}
                        </h2>

                        <p className="PlaylistScreen__Subtitle">
                            {settings.screenSubtitle || '¿QUÉ CANCIÓN O ARTISTA FALTA EN LA PLAYLIST?'}
                        </p>

                        {settings.isScreenQrVisible !== false && (
                            <div className="PlaylistScreen__QRCard">
                                <div className="PlaylistScreen__QRFrame">
                                    <QRCodeSVG
                                        value={qrTargetUrl}
                                        size={340}
                                        level="H"
                                        bgColor="#FFFFFF"
                                        fgColor="#0B1528"
                                        imageSettings={{
                                            src: qrCenterLogo,
                                            x: undefined,
                                            y: undefined,
                                            height: 72,
                                            width: 72,
                                            excavate: true
                                        }}
                                    />
                                </div>

                                <div className="PlaylistScreen__QRInstructions">
                                    <div className="PlaylistScreen__QRStep">
                                        <div className="PlaylistScreen__QRStepNumber">1</div>
                                        <div className="PlaylistScreen__QRStepText">Escanea el código QR con tu celular</div>
                                    </div>
                                    <div className="PlaylistScreen__QRStep">
                                        <div className="PlaylistScreen__QRStepNumber">2</div>
                                        <div className="PlaylistScreen__QRStepText">Busca tu canción o artista favorito</div>
                                    </div>
                                    <div className="PlaylistScreen__QRStep">
                                        <div className="PlaylistScreen__QRStepNumber">3</div>
                                        <div className="PlaylistScreen__QRStepText">¡Vota y hazla sonar en el estadio!</div>
                                    </div>

                                    <div className="PlaylistScreen__QRActionPill">
                                        <i className="bi bi-phone-fill me-2"></i>
                                        ¡Suma tus canciones ahora!
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* =========================================================== */}
                {/* MODO 1: NOW PLAYING (EN REPRODUCCIÓN) */}
                {/* =========================================================== */}
                {activeScreenMode === 1 && (
                    <section className="PlaylistScreen__Mode1">
                        <div className="PlaylistScreen__Card PlaylistScreen__NowPlayingMainCard">
                            {/* Barra superior de la tarjeta */}
                            <div className="PlaylistScreen__CardTopBar">
                                <div className="PlaylistScreen__PlayingBadge">
                                    <i className="bi bi-disc-fill me-1"></i>
                                    Sonando en el estadio
                                </div>

                                {settings.showVoteCountOnScreen && activeSong?.voteCount > 0 && (
                                    <div className="PlaylistScreen__VotesBadge">
                                        🔥 {activeSong.voteCount} {activeSong.voteCount === 1 ? 'Voto' : 'Votos de la afición'}
                                    </div>
                                )}
                            </div>

                            {/* Sección del vinilo y detalles de la canción */}
                            <div className="PlaylistScreen__SongHero">
                                <div className="PlaylistScreen__VinylWrapper">
                                    <div className="PlaylistScreen__Vinyl">
                                        <div className="PlaylistScreen__VinylCenter">
                                            <div className="PlaylistScreen__VinylHole" />
                                        </div>
                                    </div>
                                </div>

                                <div className="PlaylistScreen__SongDetails">
                                    <h2 className="PlaylistScreen__SongTitle">
                                        {activeSong?.title || 'ESPERANDO CANCIÓN...'}
                                    </h2>
                                    <h3 className="PlaylistScreen__SongArtist">
                                        {activeSong?.artistName || 'Atlético de San Luis'}
                                    </h3>

                                    {/* Ecualizador animado */}
                                    <div className="PlaylistScreen__Equalizer">
                                        {Array.from({ length: 16 }).map((_, index) => (
                                            <span key={index} className="PlaylistScreen__EqualizerBar" />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Créditos de la afición */}
                            {renderFanCredits(activeSong)}
                        </div>

                        {/* QR Lateral */}
                        {settings.isScreenQrVisible !== false && (
                            <div className="PlaylistScreen__Card PlaylistScreen__LateralQRCard">
                                <h3 className="PlaylistScreen__LateralQRTitle">¡Suma tus Canciones!</h3>
                                <p className="PlaylistScreen__LateralQRSubtitle">
                                    ¡Escanea y suma tus canciones a la playlist!
                                </p>

                                <div className="PlaylistScreen__LateralQRFrame">
                                    <QRCodeSVG
                                        value={qrTargetUrl}
                                        size={220}
                                        level="H"
                                        bgColor="#FFFFFF"
                                        fgColor="#0B1528"
                                        imageSettings={{
                                            src: qrCenterLogo,
                                            x: undefined,
                                            y: undefined,
                                            height: 48,
                                            width: 48,
                                            excavate: true
                                        }}
                                    />
                                </div>

                                <div className="PlaylistScreen__LateralQRPill">
                                    📱 Pide tu canción favorita
                                </div>
                            </div>
                        )}
                    </section>
                )}

                {/* =========================================================== */}
                {/* MODO 2: QUEUE (EN COLA Y PRÓXIMAS CANCIONES) */}
                {/* =========================================================== */}
                {activeScreenMode === 2 && (
                    <section className="PlaylistScreen__Mode2">
                        <div className="PlaylistScreen__QueueMainSection">
                            {/* Canción en reproducción destacada superior */}
                            {activeSong && (
                                <div className="PlaylistScreen__NowPlayingMini">
                                    <div className="PlaylistScreen__NowPlayingMiniLeft">
                                        <div className="PlaylistScreen__MiniVinyl" />
                                        <div className="PlaylistScreen__NowPlayingMiniInfo">
                                            <span className="PlaylistScreen__NowPlayingMiniLabel">
                                                <i className="bi bi-play-circle-fill me-1"></i> Sonando Ahora
                                            </span>
                                            <h3 className="PlaylistScreen__NowPlayingMiniTitle">
                                                {activeSong.title}
                                            </h3>
                                            <p className="PlaylistScreen__NowPlayingMiniArtist">
                                                {activeSong.artistName || 'Atlético de San Luis'}
                                            </p>
                                        </div>
                                    </div>

                                    {settings.showVoteCountOnScreen && activeSong.voteCount > 0 && (
                                        <div className="PlaylistScreen__VotesBadge">
                                            🔥 {activeSong.voteCount} votos
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Lista de próximas canciones en cola */}
                            <div className="PlaylistScreen__QueueContainer">
                                <div className="PlaylistScreen__QueueHeader">
                                    <h3 className="PlaylistScreen__QueueHeaderTitle">
                                        <i className="bi bi-music-note-list text-warning me-2"></i>
                                        Próximas en Cola
                                    </h3>
                                    <span className="PlaylistScreen__QueueHeaderBadge">Top Aprobadas</span>
                                </div>

                                <div className="PlaylistScreen__QueueList">
                                    {upcomingQueue.length > 0 ? (
                                        upcomingQueue.map((song, idx) => (
                                            <div key={song.id || idx} className="PlaylistScreen__QueueItem">
                                                <div className="PlaylistScreen__QueueItemLeft">
                                                    <div className="PlaylistScreen__QueuePosition">
                                                        <span>▲</span> {idx + 1}
                                                    </div>
                                                    <div className="PlaylistScreen__QueueSongData">
                                                        <h4 className="PlaylistScreen__QueueSongTitle">{song.title}</h4>
                                                        <span className="PlaylistScreen__QueueSongArtist">
                                                            {song.artistName || 'Afición ADSL'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="PlaylistScreen__QueueItemRight">
                                                    {settings.showFanNameOnScreen && song.fanName && (
                                                        <span className="PlaylistScreen__QueueFanTag">
                                                            Sugerida por: <strong>{song.fanName}</strong>
                                                        </span>
                                                    )}
                                                    {settings.showVoteCountOnScreen && (
                                                        <span className="PlaylistScreen__QueueVotesBadge">
                                                            🔥 {song.voteCount || 0}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="PlaylistScreen__QueueEmptyState">
                                            <i className="bi bi-music-note-beamed d-block fs-1 text-warning mb-2"></i>
                                            ¡Sé el primero en proponer la siguiente canción escaneando el código QR!
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* QR Lateral */}
                        {settings.isScreenQrVisible !== false && (
                            <div className="PlaylistScreen__Card PlaylistScreen__LateralQRCard">
                                <h3 className="PlaylistScreen__LateralQRTitle">¡Sigue Proponiendo!</h3>
                                <p className="PlaylistScreen__LateralQRSubtitle">
                                    ¡Escanea y suma tus canciones a la playlist!
                                </p>

                                <div className="PlaylistScreen__LateralQRFrame">
                                    <QRCodeSVG
                                        value={qrTargetUrl}
                                        size={220}
                                        level="H"
                                        bgColor="#FFFFFF"
                                        fgColor="#0B1528"
                                        imageSettings={{
                                            src: qrCenterLogo,
                                            x: undefined,
                                            y: undefined,
                                            height: 48,
                                            width: 48,
                                            excavate: true
                                        }}
                                    />
                                </div>

                                <div className="PlaylistScreen__LateralQRPill">
                                    🗳️ Vota por la siguiente
                                </div>
                            </div>
                        )}
                    </section>
                )}

            </main>
        </div>
    );
};

PlaylistScreen.propTypes = {
    payload: PropTypes.object
};
